/**
 * The phone's own copy of the ledger, in IndexedDB.
 *
 *   events  every event the server has confirmed, per group (never edited)
 *   outbox  changes made on this device that the server hasn't confirmed yet
 *   meta    small values: sync cursor per group, last opened group
 *
 * Everything the app shows is read from here first, so it opens and works with no signal.
 */
import type { Command, StoredEvent } from "@split/core";
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

export interface OutboxEntry {
  cmdId: string;
  groupId: string;
  cmd: Command;
  createdAt: string;
  /** "pending" = waiting to send; "failed" = the server refused it (see error). */
  status: "pending" | "failed";
  error?: string;
}

interface Schema extends DBSchema {
  events: { key: [string, number]; value: StoredEvent; indexes: { byGroup: string } };
  outbox: { key: string; value: OutboxEntry; indexes: { byGroup: string } };
  meta: { key: string; value: unknown };
}

export type LocalDB = IDBPDatabase<Schema>;

export function openLocal(name = "split-ledger"): Promise<LocalDB> {
  return openDB<Schema>(name, 1, {
    upgrade(db) {
      const events = db.createObjectStore("events", { keyPath: ["groupId", "seq"] });
      events.createIndex("byGroup", "groupId");
      const outbox = db.createObjectStore("outbox", { keyPath: "cmdId" });
      outbox.createIndex("byGroup", "groupId");
      db.createObjectStore("meta");
    },
  });
}

export async function loadEvents(db: LocalDB, groupId: string): Promise<StoredEvent[]> {
  const range = IDBKeyRange.bound([groupId, 0], [groupId, Number.MAX_SAFE_INTEGER]);
  return db.getAll("events", range);
}

export async function saveEvents(
  db: LocalDB,
  events: StoredEvent[],
  cursorKey: string,
  cursor: number,
) {
  const tx = db.transaction(["events", "meta"], "readwrite");
  for (const e of events) await tx.objectStore("events").put(e);
  await tx.objectStore("meta").put(cursor, cursorKey);
  await tx.done;
}

/**
 * Everything this device has for one group, read in one transaction so it is one consistent
 * picture. Read separately, a save from elsewhere (a background catch-up, another tab, an engine
 * stopped mid-request) could land between the reads: the sync position would then say "have
 * everything up to here" with none of those events, and the group would open empty and stay so.
 */
export async function loadGroup(
  db: LocalDB,
  groupId: string,
  metaKeys: { cursor: string; synced: string },
): Promise<{
  events: StoredEvent[];
  outbox: OutboxEntry[];
  cursor: number | undefined;
  synced: string | undefined;
}> {
  const tx = db.transaction(["events", "outbox", "meta"], "readonly");
  const range = IDBKeyRange.bound([groupId, 0], [groupId, Number.MAX_SAFE_INTEGER]);
  // (tx.done in the same Promise.all, so a failed read can't leave it rejecting unhandled)
  const [events, outbox, cursor, synced] = await Promise.all([
    tx.objectStore("events").getAll(range),
    tx.objectStore("outbox").index("byGroup").getAll(groupId),
    tx.objectStore("meta").get(metaKeys.cursor),
    tx.objectStore("meta").get(metaKeys.synced),
    tx.done,
  ]);
  return {
    events,
    outbox: sortOutbox(outbox),
    cursor: cursor as number | undefined,
    synced: synced as string | undefined,
  };
}

const sortOutbox = (all: OutboxEntry[]) =>
  all.sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0));

export async function loadOutbox(db: LocalDB, groupId: string): Promise<OutboxEntry[]> {
  return sortOutbox(await db.getAllFromIndex("outbox", "byGroup", groupId));
}

export const putOutbox = (db: LocalDB, e: OutboxEntry) => db.put("outbox", e);
export const deleteOutbox = (db: LocalDB, cmdId: string) => db.delete("outbox", cmdId);

export async function getMeta<T>(db: LocalDB, key: string): Promise<T | undefined> {
  return (await db.get("meta", key)) as T | undefined;
}
export const setMeta = (db: LocalDB, key: string, value: unknown) => db.put("meta", value, key);
